import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import * as moment from 'moment';
import { DetailActivity } from 'src/app/home/order/order.model';
import { OrderService } from 'src/app/home/order/order.service';
import { TimeService } from 'src/app/core/services/time.service';
import { decodeBase64 } from 'src/app/core/services/utils.service';

export interface GuestOrderReference {
  orderTranId?: number;
  // Retained only for legacy references or activities without an order transaction.
  activityId?: number;
  channelTag?: string;
  tranDate: string;
}

@Injectable({ providedIn: 'root' })
export class GuestOrderService {
  private readonly storageKey = 'guest-order';
  private readonly subscriptionKey = 'guest-order-subscription';
  private readonly cacheDurationHours = 6;
  private requestVersion = 0;
  private pendingRequests = new Map<string, Promise<DetailActivity[]>>();

  constructor(
    private orderService: OrderService,
    private timeService: TimeService,
    @Inject(PLATFORM_ID) private platformId: Object
  ) { }

  setSubscription(subscriptionTag: string, previousSubscriptionTag?: string): void {
    if (!isPlatformBrowser(this.platformId) || !subscriptionTag) return;
    const storedSubscription = localStorage.getItem(this.subscriptionKey) || previousSubscriptionTag;
    if ((storedSubscription && storedSubscription !== subscriptionTag)
      || (!storedSubscription && localStorage.getItem(this.storageKey))) {
      // Legacy entries can only be retained when their previous subscription is known.
      this.clear();
    }
    localStorage.setItem(this.subscriptionKey, subscriptionTag);
  }

  clear(): void {
    this.requestVersion++;
    this.pendingRequests.clear();
    if (isPlatformBrowser(this.platformId)) localStorage.removeItem(this.storageKey);
  }

  // Reading also migrates the old Base64 payloads and removes expired entries.
  getReferences(): GuestOrderReference[] {
    if (!isPlatformBrowser(this.platformId)) return [];
    const stored = localStorage.getItem(this.storageKey);
    if (!stored) return [];

    let entries: any;
    try {
      entries = JSON.parse(stored);
    } catch {
      entries = stored;
    }

    const references = this.normalize(this.unpack(entries));
    this.persist(references);
    return references;
  }

  save(orders: DetailActivity[], channelTag?: string): void {
    if (!isPlatformBrowser(this.platformId)) return;
    const references = this.normalize([
      ...this.getReferences(),
      ...orders.map(order => ({ ...order, channelTag: order.channelTag || channelTag }))
    ]);
    this.persist(references);
  }

  async getOrders(channelTag?: string): Promise<DetailActivity[]> {
    if (!isPlatformBrowser(this.platformId)) return [];
    const requestVersion = this.requestVersion;
    const subscriptionTag = localStorage.getItem(this.subscriptionKey);
    const references = this.getReferences();
    const results = await this.loadReferencedOrders(references, channelTag);

    if (requestVersion !== this.requestVersion || subscriptionTag !== localStorage.getItem(this.subscriptionKey)) return [];
    // Do not redisplay orders removed by logout/channel changes during a request.
    const currentReferences = this.getReferences();
    const currentKeys = new Set(currentReferences.map(reference => this.key(reference)));
    this.migrateLegacyReferences(references, results, currentReferences);

    const orders: DetailActivity[] = [];
    const seen = new Set<string>();
    for (let index = 0; index < references.length; index++) {
      const reference = references[index];
      const details = results[index];
      const isCurrent = currentKeys.has(this.key(reference));
      // Another reader may have already upgraded this activity reference to an order reference.
      const wasMigrated = !reference.orderTranId && details.some(detail =>
        this.isValidId(detail.orderTranId) && currentKeys.has(this.key({ ...reference, orderTranId: detail.orderTranId })));
      if (!isCurrent && !wasMigrated) continue;

      for (const detail of details) {
        const key = JSON.stringify([reference.channelTag || channelTag, detail.activityId || detail.orderTranId]);
        if (seen.has(key)) continue;
        seen.add(key);
        orders.push(detail);
      }
    }
    return orders;
  }

  private async loadReferencedOrders(references: GuestOrderReference[], channelTag?: string): Promise<DetailActivity[][]> {
    const groups = new Map<string, Set<number>>();
    for (const reference of references) {
      if (!reference.orderTranId) continue;
      const tag = reference.channelTag || channelTag;
      if (!groups.has(tag)) groups.set(tag, new Set<number>());
      groups.get(tag).add(reference.orderTranId);
    }
    const batches = new Map<string, Promise<DetailActivity[]>>();
    groups.forEach((group, tag) => {
      const ids = Array.from(group).sort((a, b) => a - b);
      batches.set(tag, this.loadRequest(['orders', tag, ids], () => this.orderService.getActivityByOrders(ids, tag, false)));
    });
    return Promise.all(references.map(async reference => {
      if (reference.orderTranId) {
        const orders = await batches.get(reference.channelTag || channelTag);
        return orders.filter(order => order.orderTranId === reference.orderTranId);
      }
      const orders = await this.loadDetail(reference, channelTag);
      return orders.filter(order => order.activityId === reference.activityId);
    }));
  }

  private migrateLegacyReferences(
    references: GuestOrderReference[],
    results: DetailActivity[][],
    currentReferences: GuestOrderReference[]
  ): void {
    const currentKeys = new Set(currentReferences.map(reference => this.key(reference)));
    const migrated = new Map<string, GuestOrderReference>();
    references.forEach((reference, index) => {
      if (reference.orderTranId || !currentKeys.has(this.key(reference))) return;
      const detail = results[index].find(order => this.isValidId(order.orderTranId));
      if (detail) {
        migrated.set(this.key(reference), { ...reference, orderTranId: detail.orderTranId });
      }
    });
    if (migrated.size) {
      this.persist(this.normalize(currentReferences.map(reference => migrated.get(this.key(reference)) || reference)));
    }
  }

  private loadDetail(reference: GuestOrderReference, channelTag?: string): Promise<DetailActivity[]> {
    const resolvedTag = reference.channelTag || channelTag;
    return this.loadRequest(['activity', resolvedTag, reference.activityId],
      () => this.orderService.getActivityDetail(reference.activityId, false, resolvedTag));
  }

  private loadRequest(identity: any[], fetch: () => Promise<any>): Promise<DetailActivity[]> {
    const key = JSON.stringify([this.requestVersion, localStorage.getItem(this.subscriptionKey), ...identity]);
    const pending = this.pendingRequests.get(key);
    if (pending) return pending;

    const request = fetch()
      .then(result => Array.isArray(result) ? result : [])
      // Keep the reference on API failure so a later visit can retry.
      .catch(() => [])
      .finally(() => this.pendingRequests.delete(key));
    this.pendingRequests.set(key, request);
    return request;
  }

  private unpack(value: any): any[] {
    if (Array.isArray(value)) return value.reduce((entries, entry) => entries.concat(this.unpack(entry)), []);
    if (typeof value === 'string') {
      try {
        const decoded = decodeBase64(value);
        return decoded ? this.unpack(JSON.parse(decoded)) : [];
      } catch {
        return [];
      }
    }
    return value && typeof value === 'object' ? [value] : [];
  }

  private normalize(entries: any[]): GuestOrderReference[] {
    const references = new Map<string, GuestOrderReference>();
    const now = this.timeService.getAdjustedLocalTimeInUtc().toISOString();
    for (const entry of entries) {
      if ((!this.isValidId(entry.orderTranId) && !this.isValidId(entry.activityId))
        || typeof entry.tranDate !== 'string' || !moment(entry.tranDate, moment.ISO_8601, true).isValid()) continue;
      const tranDate = this.timeService.convertToUtc(moment(entry.tranDate));
      if (this.timeService.compareIsTimeAfter(now, tranDate.toISOString(), this.cacheDurationHours, 'hours')) continue;

      const reference: GuestOrderReference = {
        ...(this.isValidId(entry.orderTranId) ? { orderTranId: entry.orderTranId } : { activityId: entry.activityId }),
        channelTag: typeof entry.channelTag === 'string' ? entry.channelTag : undefined,
        tranDate: entry.tranDate
      };
      references.set(this.key(reference), reference);
    }
    return Array.from(references.values());
  }

  private key(reference: GuestOrderReference): string {
    return JSON.stringify([reference.channelTag || '', reference.orderTranId ? 'order' : 'activity', reference.orderTranId || reference.activityId]);
  }

  private isValidId(id: number): boolean {
    return Number.isSafeInteger(id) && id > 0;
  }

  private persist(references: GuestOrderReference[]): void {
    if (references.length === 0) {
      localStorage.removeItem(this.storageKey);
    } else {
      const value = JSON.stringify(references);
      if (localStorage.getItem(this.storageKey) !== value) localStorage.setItem(this.storageKey, value);
    }
  }
}
